import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Building2, Sparkles, Calendar, ArrowLeft, X, Map, MapPin, Terminal, ClipboardList, BarChart3 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/contexts/AuthContext';

interface SidebarProps {
  barId?: string;
  isOpen?: boolean;
  onClose?: () => void;
}

export function Sidebar({ barId, isOpen = false, onClose }: SidebarProps) {
  const { dbUser } = useAuth();

  const mainLinks = [
    { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
    ...(dbUser?.isAdmin ? [
      { to: '/admin/metros', label: 'Metros', icon: Map, end: false },
      { to: '/admin/locations', label: 'Locations', icon: MapPin, end: false },
      { to: '/admin/scripts', label: 'Scripts', icon: Terminal, end: false },
      { to: '/admin/discovery-log', label: 'Discovery Log', icon: ClipboardList, end: false },
      { to: '/admin/analytics', label: 'Analytics', icon: BarChart3, end: false },
    ] : []),
  ];

  const barLinks = barId
    ? [
        { to: `/admin/bars/${barId}`, label: 'Bar Details', icon: Building2, end: true },
        { to: `/admin/bars/${barId}/specials`, label: 'Specials', icon: Sparkles },
        { to: `/admin/bars/${barId}/events`, label: 'Events', icon: Calendar },
      ]
    : [];

  // Close sidebar when navigating on mobile
  const handleNavClick = () => {
    if (onClose) onClose();
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-50 h-screen w-64 border-r border-[var(--color-border)] bg-[var(--color-card)]',
        'transform transition-transform duration-200 ease-in-out',
        'lg:translate-x-0', // Always visible on desktop
        isOpen ? 'translate-x-0' : '-translate-x-full' // Toggle on mobile
      )}
    >
      {/* Header */}
      <div className="flex h-14 items-center justify-between border-b border-[var(--color-border)] px-4">
        <span className="text-xl font-bold text-accent-primary">Bar Guide Admin</span>
        {/* Close button for mobile */}
        <button
          className="lg:hidden p-1 rounded hover:bg-[var(--color-border)]"
          onClick={onClose}
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="p-4">
        {/* Back to site */}
        <NavLink
          to="/"
          onClick={handleNavClick}
          className="mb-4 flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-[var(--color-muted)] hover:bg-[var(--color-border)] hover:text-[var(--color-text)]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Site
        </NavLink>

        {/* Main links */}
        <div className="space-y-1">
          {mainLinks.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={handleNavClick}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-accent-primary/10 text-accent-primary'
                    : 'hover:bg-[var(--color-border)]'
                )
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </div>

        {/* Bar-specific links */}
        {barLinks.length > 0 && (
          <div className="mt-6">
            <p className="mb-2 px-3 text-xs font-medium uppercase text-[var(--color-muted)]">
              Current Bar
            </p>
            <div className="space-y-1">
              {barLinks.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-accent-primary/10 text-accent-primary'
                        : 'hover:bg-[var(--color-border)]'
                    )
                  }
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* User info */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-[var(--color-border)] p-4">
        <div className="text-sm">
          <p className="font-medium">{dbUser?.name || dbUser?.email}</p>
          <p className="text-xs text-[var(--color-muted)]">
            {dbUser?.isAdmin ? 'Admin' : 'Bar Owner'}
          </p>
        </div>
      </div>
    </aside>
  );
}
